import { Ref, useId, useState } from 'react';
import { CalendarMonthOutlined, Close } from '@mui/icons-material';
import {
  Dialog,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useForkRef } from '@mui/material/utils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { esES } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import DateRangePickerPanel from './DateRangePickerPanel';
import {
  DateRangeErrors,
  DateRangeOptions,
  DateRangeValue,
  getDateRangeErrors,
  isValidRangeDate,
} from './dateRangeValidation';

export interface DateRangeInputProps extends DateRangeOptions {
  fromName: string;
  toName: string;
  placeholder?: string;
  fromPlaceholder?: string;
  toPlaceholder?: string;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  onBlur?: () => void;
  fromRef?: Ref<HTMLInputElement>;
  toRef?: Ref<HTMLInputElement>;
  errors?: DateRangeErrors;
  disabled?: boolean;
  autoFocus?: boolean;
  clearable?: boolean;
  helperText?: string;
}

const DateRangeInput = ({
  fromName,
  toName,
  placeholder = 'Período',
  fromPlaceholder = 'Inicio',
  toPlaceholder = 'Fin',
  value,
  onChange,
  onBlur,
  fromRef,
  toRef,
  errors = {},
  disabled = false,
  autoFocus = false,
  clearable = false,
  helperText,
  withTime = false,
  allowOpenRange = false,
  required = false,
  blockedRanges = [],
}: DateRangeInputProps) => {
  const id = useId();
  const inputRef = useForkRef(fromRef, toRef);
  const mobile = useMediaQuery('(max-width: 599px)');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [touched, setTouched] = useState(false);
  const open = !!anchor && !disabled;
  const rangeErrors =
    touched || value.from || value.to
      ? getDateRangeErrors(value, {
          withTime,
          allowOpenRange,
          required,
          blockedRanges,
        })
      : {};
  const message =
    errors.from || errors.to || rangeErrors.from || rangeErrors.to;
  const formatDate = (date: Date | null, empty: string) => {
    if (!date) return empty;
    if (!isValidRangeDate(date)) return 'Fecha inválida';
    return dayjs(date).format(
      withTime
        ? `DD/MM/YYYY HH:mm${date.getSeconds() ? ':ss' : ''}`
        : 'DD/MM/YYYY'
    );
  };
  const summary =
    value.from || value.to
      ? `${formatDate(value.from, 'Sin inicio')}${mobile ? '\n' : ' '}→ ${formatDate(value.to, 'Sin fin')}`
      : touched && allowOpenRange
        ? 'Sin límite de fechas'
        : '';
  const commit = (range: DateRangeValue) => {
    setTouched(true);
    onChange(range);
    onBlur?.();
    setAnchor(null);
  };
  const picker = open && (
    <LocalizationProvider
      localeText={
        esES.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <DateRangePickerPanel
        value={value}
        label={placeholder}
        titleId={`${id}-title`}
        fromLabel={fromPlaceholder}
        toLabel={toPlaceholder}
        withTime={withTime}
        allowOpenRange={allowOpenRange}
        required={required}
        blockedRanges={blockedRanges}
        onApply={commit}
        onCancel={() => setAnchor(null)}
      />
    </LocalizationProvider>
  );

  return (
    <>
      <TextField
        id={id}
        name={`${fromName}-${toName}`}
        label={placeholder}
        placeholder='Seleccionar inicio y fin'
        value={summary}
        multiline={mobile}
        minRows={mobile ? 2 : undefined}
        inputRef={inputRef}
        fullWidth
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        error={!!message}
        helperText={message || helperText}
        onClick={(event) => {
          if (!disabled) setAnchor(event.currentTarget);
        }}
        onKeyDown={(event) => {
          if (['Enter', ' ', 'ArrowDown'].includes(event.key) && !disabled) {
            event.preventDefault();
            setAnchor(event.currentTarget);
          }
        }}
        inputProps={{
          role: 'combobox',
          'aria-expanded': open,
          'aria-haspopup': 'dialog',
          'aria-controls': open ? `${id}-calendar` : undefined,
          title: summary || 'Seleccionar inicio y fin',
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position='end'>
              {clearable && (value.from || value.to) && !disabled && (
                <IconButton
                  size='small'
                  aria-label='Limpiar período'
                  onClick={(event) => {
                    event.stopPropagation();
                    commit({ from: null, to: null });
                  }}
                >
                  <Close fontSize='small' />
                </IconButton>
              )}
              <IconButton
                aria-label='Elegir período'
                disabled={disabled}
                tabIndex={-1}
                edge='end'
              >
                <CalendarMonthOutlined />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& input, & textarea': {
            cursor: disabled ? 'default' : 'pointer',
            textOverflow: 'ellipsis',
            fontSize: { xs: 14, sm: 16 },
          },
        }}
      />
      {mobile ? (
        <Dialog
          open={open}
          onClose={() => setAnchor(null)}
          aria-labelledby={`${id}-title`}
          PaperProps={{
            id: `${id}-calendar`,
            sx: { m: 1.5, width: 360, maxWidth: 'calc(100% - 24px)' },
          }}
        >
          {picker}
        </Dialog>
      ) : (
        <Popover
          open={open}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              id: `${id}-calendar`,
              role: 'dialog',
              'aria-modal': true,
              'aria-labelledby': `${id}-title`,
              sx: { width: 360, mt: 0.75, borderRadius: 2 },
            },
          }}
        >
          {picker}
        </Popover>
      )}
    </>
  );
};

export default DateRangeInput;
